<template>
  <div ref="dropdownRef" class="change-active-diet">
    <Tooltip placement="bottom-end">
      <template #trigger>
        <StyledButton class="min-w-0" left-icon="list" variant="secondary" @click="toggleOpen" />
      </template>
      Przełącz jadłospis
    </Tooltip>
    <div v-if="isOpen" class="change-active-diet__dropdown">
      <h3 class="change-active-diet__title">Przełącz jadłospis</h3>

      <div class="change-active-diet__list">
        <template v-if="isLoading && diets.length === 0">
          <div class="change-active-diet__loading">Ładowanie…</div>
        </template>
        <template v-else-if="!isLoading && diets.length === 0">
          <div class="change-active-diet__empty">Brak jadłospisów</div>
        </template>
        <template v-else>
          <ActiveDietListItem
            v-for="diet in diets"
            :key="diet.id"
            :diet="diet"
            @select="selectDiet"
          />
        </template>
      </div>

      <StyledButton
        v-if="hasNextPage"
        variant="outline"
        :disabled="isFetchingNextPage"
        @click="fetchNextPage"
      >
        {{ isFetchingNextPage ? 'Ładowanie…' : 'Załaduj więcej' }}
      </StyledButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { useInfiniteQuery } from '@tanstack/vue-query'
  import { onClickOutside } from '@vueuse/core'
  import { computed, ref } from 'vue'

  import { StyledButton } from '@/components/StyledButton'
  import { Tooltip } from '@/components/ui'
  import { dietsService, type PatientDietListItem } from '@/services/dietsService'

  import ActiveDietListItem from './components/ActiveDietListItem/ActiveDietListItem.vue'
  import type { ActiveDietListPage } from './types'
  import { toActiveDietItem } from './utils'

  const PAGE_SIZE = 5

  const props = withDefaults(
    defineProps<{
      dietId: number
      activeDietId?: number | null
      patientId?: number | null
    }>(),
    { activeDietId: null, patientId: null },
  )

  const emit = defineEmits<{
    select: [dietId: number]
  }>()

  const dropdownRef = ref<HTMLElement | null>(null)
  const isOpen = ref(false)

  const activeId = () => props.activeDietId ?? props.dietId

  // Use patientId if available, otherwise fallback to dietId for query key stability
  const queryKeyId = computed(() => props.patientId ?? props.dietId)

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery({
    enabled: computed(() => isOpen.value && !!props.dietId),
    gcTime: 1000 * 60 * 30, // 30 minutes - keep in cache longer
    getNextPageParam: (lastPage: ActiveDietListPage, allPages) => {
      // If there's a next URL, return the next page number
      if (lastPage.next) {
        return allPages.length + 1
      }
      return undefined
    },
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const { data: responseData } = await dietsService.fetchPatientDietList(
        props.dietId,
        pageParam,
        PAGE_SIZE,
      )
      const paginatedResponse = responseData
      return {
        count: paginatedResponse.count,
        next: paginatedResponse.next,
        previous: paginatedResponse.previous,
        results: paginatedResponse.results.map((item: PatientDietListItem) =>
          toActiveDietItem(item, activeId()),
        ),
      }
    },
    queryKey: computed(() => ['patientDietList', queryKeyId.value]),
    staleTime: 1000 * 60 * 5, // 5 minutes - keep data fresh longer
  })

  const diets = computed(() => {
    const pages = data.value?.pages ?? []
    const currentActiveId = activeId()
    return pages.flatMap((page: ActiveDietListPage) =>
      page.results.map(diet => ({
        ...diet,
        isActive: diet.id === currentActiveId,
      })),
    )
  })

  function toggleOpen() {
    isOpen.value = !isOpen.value
  }

  onClickOutside(dropdownRef, () => {
    isOpen.value = false
  })

  function selectDiet(dietId: number) {
    emit('select', dietId)
    isOpen.value = false
  }
</script>

<style scoped>
  .change-active-diet {
    position: relative;
    display: inline-flex;
  }

  .change-active-diet__dropdown {
    position: absolute;
    top: 100%;
    right: 0;
    z-index: 50;
    display: flex;
    flex-direction: column;
    width: 400px;
    margin-top: 8px;
    overflow: hidden;
    background: #fff;
    border: 1px solid #e9eaeb;
    border-radius: 12px;
    box-shadow: 0 4px 20px hsl(0deg 0% 0% / 12%);
  }

  .change-active-diet__title {
    padding: 10px 14px;
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #181d27;
    border-bottom: 1px solid #e9eaeb;
  }

  .change-active-diet__list {
    max-height: 280px;
    padding: 4px 6px;
    overflow-y: auto;
  }

  .change-active-diet__loading,
  .change-active-diet__empty {
    padding: 16px;
    font-size: 14px;
    color: #535862;
    text-align: center;
  }

  .change-active-diet__item {
    display: flex;
    gap: 12px;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 10px 8px;
    text-align: left;
    cursor: pointer;
    background: none;
    border: none;
    border-radius: 6px;
    transition: background 0.15s;
  }

  .change-active-diet__item:last-child {
    border-bottom: none;
  }

  .change-active-diet__item:hover,
  .change-active-diet__item--active {
    background: #fafafa;
  }

  .change-active-diet__item-content {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }

  .change-active-diet__item-name {
    margin-bottom: 2px;
    font-size: 14px;
    font-weight: 400;
    color: #535862;
    overflow-wrap: break-word;
  }

  .change-active-diet__item-dates {
    font-size: 14px;
    font-weight: 600;
    color: #414651;
  }

  .change-active-diet__item-meta {
    display: flex;
    flex-shrink: 0;
    gap: 8px;
    align-items: center;
  }

  .change-active-diet__tag {
    padding: 4px 10px;
    font-size: 12px;
    font-weight: 500;
    color: var(--color-primary-text, #1a1a1a);
    border-radius: 6px;
  }

  .change-active-diet__tag--draft {
    background: #ffecc9;
  }

  .change-active-diet__tag--ready {
    background: #c3f1db;
  }

  .change-active-diet__check {
    flex-shrink: 0;
    color: #1b412a;
  }

  .badge {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 22px;
    padding-right: 8px;
    padding-left: 8px;
    font-size: 12px;
    font-weight: 500;
    border: 1px solid;
    border-radius: 6px;
  }

  .badge--draft {
    color: var(--color-white);
    color: #b54708;
    background-color: #fffaeb;
    border-color: #fedf89;
  }

  .badge--published {
    color: #067647;
    background-color: #ecfdf3;
    border-color: #abefc6;
  }

  .badge--archived {
    color: #1a1a1a;
    background-color: #f8f8f8;
    border-color: #cacaca;
  }
</style>
